import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

// actions
import { actions as browseActions } from 'modules/browse/actions'

// Components
import { IconRow } from 'kit'
import { SampleLengthIcon } from 'components/licensing/selectsamplelength'
import Track from 'components/track'

// Selectors
import { licenseInfoSelector, LicenseSelectorInfo } from 'selectors'

// Types
import type { UserSongSample, State, Song, Track as TrackType } from 'types'

// Utils
import { createSongItemId } from 'utils'

import styles from './tracksampled.css'

type TrackSampledProps = {
  sample: UserSongSample
}

const TrackSampled = ({ sample }: TrackSampledProps): JSX.Element => {
  const dispatch = useDispatch()
  const { sharePercentage, descriptionShort, maxSeconds } = useSelector<State, LicenseSelectorInfo>(
    s => licenseInfoSelector(s, sample) || ({} as LicenseSelectorInfo),
  )
  const { sample_runtimes, song } = sample
  const sampledMasterTrackId = (song as Song & { tracks: Array<TrackType> }).tracks.find(track => track.type === 17)?.id as number
  useEffect(() => {
    if (sampledMasterTrackId) {
      dispatch(browseActions.fetchTracksBySearchQuery(`ids=${sampledMasterTrackId}`))
    }
  }, [sampledMasterTrackId, dispatch])
  const unpublished = (song as Song)?.state !== 2
  return (
    <div className={styles.wrapper}>
      <h4>Sampled song</h4>
      {unpublished && <p className={styles.warning}>This song is no longer available on Tracklib. Contact support for more info.</p>}
      <div className={styles.divider} style={{ marginTop: 0 }} />
      <Track trackId={sampledMasterTrackId} mode="compact" songItemId={createSongItemId(+sampledMasterTrackId)} />
      <div className={styles.divider} style={{ marginTop: 0 }} />
      <h4>Time sampled from original song</h4>
      <IconRow
        title={descriptionShort}
        description={`Share ${sharePercentage}% of revenues`}
        iconComponent={<SampleLengthIcon maxSeconds={maxSeconds} />}
      />
      {sample_runtimes.length ? (
        <>
          <div className={styles.divider} style={{ marginTop: 30 }} />
          <h4>Sampled parts</h4>
          <div className={styles.runtimesWrapper}>
            {sample_runtimes
              .sort(({ start_time: aStart }, { start_time: bStart }) => {
                const [aSec, aMin] = aStart.split(':')
                const [bSec, bMin] = bStart.split(':')
                const aSeconds = +aSec + +aMin * 60
                const bSeconds = +bSec + +bMin * 60
                return aSeconds - bSeconds
              })
              .map(runtime => {
                const { id, start_time, end_time, description } = runtime
                return (
                  <div key={String(id)} className={styles.runtime}>
                    <span className={styles.runtimeTime}>{`${start_time} - ${end_time}`}</span>
                    <span className={styles.runtimeDescription}>{description}</span>
                  </div>
                )
              })}
          </div>
        </>
      ) : null}
    </div>
  )
}

export default TrackSampled
